import { useNavigate } from 'react-router-dom';

import { useFormDrawerContext } from '@/app/surfaces/form-drawer-provider.js';
import { useQuickPanelContext } from '@/app/surfaces/quick-panel-provider.js';
import {
  screenRegistry,
  type ScreenKey,
  type ScreenParams,
} from '@/app/surfaces/screen-registry.js';
import type { SurfaceMode } from '@/shared/surfaces/surface-mode.js';

export interface OpenScreenOptions<TParams extends ScreenParams = ScreenParams> {
  /** Caller decides the opening mode. Defaults to page. */
  mode?: SurfaceMode;
  params?: TParams;
  replace?: boolean;
}

export interface OpenScreenResult {
  mode: SurfaceMode;
  drawerId?: string;
  quickPanelId?: string;
}

/**
 * Unified opening API:
 * - page       -> navigate via router paths
 * - drawer     -> open globally in drawer stack
 * - quickPanel -> open globally in quick panel stack
 */
export function useSurfaceNavigation() {
  const navigate = useNavigate();
  const { openFormDrawer, closeFormDrawer, closeAllFormDrawers } = useFormDrawerContext();
  const { openQuickPanel, closeQuickPanel, closeAllQuickPanels } = useQuickPanelContext();

  function openScreen<TParams extends ScreenParams = ScreenParams>(
    screenKey: ScreenKey,
    options: OpenScreenOptions<TParams> = {},
  ): OpenScreenResult {
    const definition = screenRegistry[screenKey];
    const mode = options.mode ?? 'page';
    const params = (options.params ?? {}) as TParams;

    if (!definition.supports.includes(mode)) {
      throw new Error(`Screen "${screenKey}" does not support "${mode}" mode.`);
    }

    if (mode === 'page') {
      const toPath = definition.toPath;
      if (typeof toPath !== 'function') {
        throw new Error(`Screen "${screenKey}" is missing "toPath" for page mode.`);
      }

      void navigate(toPath(params), { replace: options.replace ?? false });
      return { mode: 'page' };
    }

    if (mode === 'quickPanel') {
      const quickPanel = definition.quickPanel;
      if (!quickPanel) {
        throw new Error(`Screen "${screenKey}" is missing quickPanel configuration.`);
      }

      const quickPanelParams = quickPanel.mapParams ? quickPanel.mapParams(params) : params;
      openQuickPanel(quickPanel.key, quickPanelParams);

      return { mode: 'quickPanel' };
    }

    const drawer = definition.drawer;
    if (!drawer) {
      throw new Error(`Screen "${screenKey}" is missing drawer configuration.`);
    }

    const drawerParams = drawer.mapParams ? drawer.mapParams(params) : params;
    const drawerId = openFormDrawer(drawer.key, drawerParams);
    return { mode: 'drawer', drawerId };
  }

  function closeSurface(mode: SurfaceMode = 'drawer') {
    if (mode === 'drawer') {
      closeFormDrawer();
      return;
    }

    if (mode === 'quickPanel') {
      closeQuickPanel();
      return;
    }

    void navigate(-1);
  }

  return {
    openScreen,
    closeSurface,
    closeFormDrawer,
    closeAllFormDrawers,
    closeQuickPanel,
    closeAllQuickPanels,
  };
}
